import React, { useState, useEffect } from 'react';
import { CommonInput } from '@/components/ui/common-input';
import { CommonSelect } from '@/components/ui/common-select';
import { CommonMultiSelect } from '@/components/ui/common-multi-select';
import { CommonButton } from '@/components/ui/common-button';
import { useToast } from '@/components/ui/use-toast';
import masterDataService from '@/services/masterDataService';
import DriverProfile from './profiles/DriverProfile';
import TransporterProfile from './profiles/TransporterProfile';
import DistributorProfile from './profiles/DistributorProfile';
import RetailerProfile from './profiles/RetailerProfile';
import { useAuth } from '@/contexts/AuthContext';

const AddUserForm = ({ onAddUser, onCancel, isSubmitting }) => {
  const { toast } = useToast();
  const { userProfile } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    phone_number: '',
    role: '',
    password: '',
    is_active: true,
    driver_profile: {
      license_number: '',
      license_expiry: '',
      transporter_id: ''
    },
    transporter_profile: {
      company_name: '',
      registration_number: '',
      contact_person: '',
      address: ''
    },
    distributor_profile: {
      company_name: '',
      contact_person: '',
      address: '',
      district_id: '',
      latitude: '',
      longitude: ''
    },
    retailer_profile: {
      company_name: '',
      contact_person: '',
      address: '',
      latitude: '',
      longitude: '',
      state_id: '',
      district_id: ''
    },
    port_ids: [],
    state_ids: []
  });
  const [errors, setErrors] = useState({});
  const [masterData, setMasterData] = useState({
    transporters: [],
    distributors: [],
    retailers: [],
    ports: [],
    states: []
  });
  const [loading, setLoading] = useState({
    transporters: false,
    distributors: false,
    retailers: false,
    ports: false
  });

  useEffect(() => {
    fetchMasterData();
  }, []);

  useEffect(() => {
    const currentUserRole = userProfile?.role;
    if (currentUserRole === 'transporter') {
      setFormData(prev => {
        if (prev.role === '') {
          return { ...prev, role: 'driver', password: 'Driver@123' };
        }
        return prev;
      });
    }
  }, [userProfile]);

  const fetchMasterData = async () => {
    try {
      setLoading(prev => ({ ...prev, transporters: true }));
      const transporters = await masterDataService.getTransporters(0, 500, false);
      
      let distributors = [];
      try {
        distributors = await masterDataService.getDistributors(0, 500, false);
      } catch (err) {
        console.error("Failed to load distributors on mount:", err);
      }
      
      let retailers = [];
      try {
        retailers = await masterDataService.getRetailers(0, 500, false);
      } catch (err) {
        console.error("Failed to load retailers on mount:", err);
      }
      
      const portsResponse = await masterDataService.getPorts(0, 500);
      
      let portsData = [];
      if (Array.isArray(portsResponse)) {
        portsData = portsResponse;
      } else if (portsResponse && typeof portsResponse === 'object') {
        if (Array.isArray(portsResponse.data)) {
          portsData = portsResponse.data;
        } else if (Array.isArray(portsResponse.ports)) {
          portsData = portsResponse.ports;
        } else {
          portsData = [];
        }
      }

      const statesData = await masterDataService.getStates(true);
      
      setMasterData({
        transporters,
        distributors,
        retailers,
        ports: portsData,
        states: Array.isArray(statesData) ? statesData : []
      });
    } catch (error) {
      console.error('Failed to fetch master data:', error);
      toast({
        title: "Error",
        description: "Failed to load master data. Some dropdowns may not be populated.",
        variant: "destructive",
      });
    } finally {
      setLoading(prev => ({ 
        transporters: false, 
        distributors: false,
        retailers: false,
        ports: false
      }));
    }
  };

  const getAllRoleOptions = () => [
    { value: 'IPL_SUPER_ADMIN', label: 'Super Admin' },
    { value: 'port_admin', label: 'Port Admin' },
    { value: 'FIELD_OFFICER', label: 'Field Officer' },
    { value: 'ACCOUNT_HEAD', label: 'Account Head' },
    { value: 'REGION_HEAD', label: 'Region Head' },
    { value: 'DRIVER', label: 'Driver' },
    { value: 'TRANSPORTER', label: 'Transporter' },
    { value: 'DISTRIBUTOR', label: 'Distributor' },
    { value: 'RETAILER', label: 'Retailer' },
    { value: 'ACCOUNT_OFFICER', label: 'Account Officer' }
  ];

  const getFilteredRoleOptions = () => {
    const currentUserRole = userProfile?.role?.toLowerCase();
    
    if (currentUserRole === 'ipl_super_admin' || currentUserRole === 'ipl_admin') {
      return getAllRoleOptions();
    } 
    else if (currentUserRole === 'transporter') {
      return [{ value: 'DRIVER', label: 'Driver' }];
    } 
    else if (currentUserRole === 'port_admin') {
      return [
        { value: 'TRANSPORTER', label: 'Transporter' },
        { value: 'DISTRIBUTOR', label: 'Distributor' },
        { value: 'DRIVER', label: 'Driver' }
      ];
    } 
    else if (['region_head', 'field_officer', 'account_head'].includes(currentUserRole)) {
      return [
        { value: 'TRANSPORTER', label: 'Transporter' },
        { value: 'DISTRIBUTOR', label: 'Distributor' },
        { value: 'RETAILER', label: 'Retailer' }
      ];
    }
    else {
      return [];
    }
  };

  const roleOptions = getFilteredRoleOptions();

  const statusOptions = [
    { value: true, label: 'Active' },
    { value: false, label: 'Inactive' }
  ];

  const transporterOptions = masterData.transporters.map(transporter => ({
    value: transporter.id,
    label: `${transporter.company_name} (${transporter.user?.username || transporter.id})`
  }));

  const distributorOptions = masterData.distributors.map(distributor => ({
    value: distributor.id,
    label: `${distributor.company_name} (${distributor.user?.username || distributor.id})`
  }));
  
  const portOptions = masterData.ports
    .filter(port => port && port.id) 
    .map(port => ({
      value: port.id.toString(), 
      label: `${port.port_name || port.id} (${port.port_code || 'No Code'})`
    }));

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    }
    
    const roleLower = formData.role ? formData.role.toLowerCase() : '';
    const isDriver = roleLower === 'driver';
    const isDistributor = roleLower === 'distributor';
    const isRetailer = roleLower === 'retailer';
    const isPortAdmin = roleLower === 'port_admin';
    const isFieldStaff = ['region_head', 'field_officer', 'account_head'].includes(roleLower);

    if (!isDriver && !isDistributor) {
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Email is invalid';
      }
    } else if (formData.email && formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!isDriver) {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }
    }

    if (isDriver) {
      if (!formData.driver_profile.license_number.trim()) {
        newErrors.license_number = 'License number is required for drivers';
      }
      
      if (!formData.driver_profile.license_expiry) {
        newErrors.license_expiry = 'License expiry is required for drivers';
      } else if (!/^\d{4}\/\d{1,2}\/\d{1,2}$/.test(formData.driver_profile.license_expiry)) {
        newErrors.license_expiry = 'License expiry must be in YYYY/MM/DD format';
      } else {
        const [year, month, day] = formData.driver_profile.license_expiry.split('/').map(Number);
        
        if (year > 2060) {
          newErrors.license_expiry = 'Year must be 2060 or earlier';
        } else if (month < 1 || month > 12) {
          newErrors.license_expiry = 'Month must be between 1 and 12';
        } else if (day < 1 || day > 31) {
          newErrors.license_expiry = 'Day must be between 1 and 31';
        } else {
          const date = new Date(year, month - 1, day);
          const isValidDate = date.getFullYear() === year && 
                              date.getMonth() === month - 1 && 
                              date.getDate() === day;
          
          if (!isValidDate) {
            newErrors.license_expiry = 'Please enter a valid date';
          } else if (date < new Date()) {
            newErrors.license_expiry = 'License expiry date cannot be in the past';
          }
        }
      }
      
      const currentUserRole = userProfile?.role?.toLowerCase();
      if (currentUserRole === 'ipl_super_admin' || currentUserRole === 'ipl_admin') {
        if (!formData.driver_profile.transporter_id) {
          newErrors.transporter_id = 'Transporter is required for drivers';
        }
      }
    } else if (roleLower === 'transporter') {
      if (!formData.transporter_profile.company_name.trim()) {
        newErrors.company_name = 'Company name is required';
      }
      if (!formData.transporter_profile.registration_number.trim()) {
        newErrors.registration_number = 'Registration number is required';
      }
      if (!formData.transporter_profile.contact_person.trim()) {
        newErrors.contact_person = 'Contact person is required';
      }
      if (!formData.transporter_profile.address.trim()) {
        newErrors.address = 'Address is required';
      }
    } else if (isDistributor) {
      if (!formData.distributor_profile.company_name.trim()) {
        newErrors.company_name = 'Company name is required for distributors';
      }
      if (!formData.distributor_profile.district_id) {
        newErrors.district_id = 'District is required for distributors';
      }
    } else if (isRetailer) {
      if (!formData.retailer_profile.company_name.trim()) {
        newErrors.company_name = 'Company name is required for retailers';
      }
      if (!formData.retailer_profile.state_id) {
        newErrors.state_id = 'State is required for retailers';
      }
      if (!formData.retailer_profile.district_id) {
        newErrors.district_id = 'District is required for retailers';
      }
    } else if (isPortAdmin && formData.port_ids.length === 0) {
      newErrors.port_ids = 'At least one port is required for port admin';
    } else if (isFieldStaff && (!formData.state_ids || formData.state_ids.length === 0)) {
      newErrors.state_ids = 'At least one state is required for Field Officer';
    }
    
    if (!formData.phone_number.trim()) {
      newErrors.phone_number = 'Phone number is required';
    } else if (formData.phone_number.length !== 10) {
      newErrors.phone_number = 'Phone number must be exactly 10 digits';
    } else if (!/^[0-9]{10}$/.test(formData.phone_number)) {
      newErrors.phone_number = 'Phone number must contain exactly 10 digits';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    if (field === 'role' && value?.toLowerCase() === 'driver') {
      setFormData(prev => ({ ...prev, [field]: value, password: 'Driver@123' }));
    } else if (field === 'phone_number') {
      const sanitizedValue = value.replace(/\D/g, '');
      setFormData(prev => ({ ...prev, [field]: sanitizedValue }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleProfileChange = (profileType, field, value) => {
    setFormData(prev => ({
      ...prev,
      [profileType]: {
        ...prev[profileType],
        [field]: value
      }
    }));

    const errorField = field;
    if (errors[errorField]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorField];
        return newErrors;
      });
    }
  };
  
  const handlePortSelection = (selectedPortIds) => {
    setFormData(prev => ({ ...prev, port_ids: selectedPortIds }));
    
    if (errors.port_ids) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.port_ids;
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      const roleLower = formData.role.toLowerCase();
      const newUserData = {
        username: formData.username,
        phone_number: formData.phone_number,
        role: formData.role,
        is_active: formData.is_active
      };
      
      if (roleLower !== 'driver') {
        newUserData.email = formData.email;
        newUserData.password = formData.password;
      } else {
        newUserData.password = 'Driver@123';
      }
      
      if (roleLower === 'port_admin') {
        newUserData.port_ids = Array.isArray(formData.port_ids) 
          ? formData.port_ids 
          : [];
      }
      
      if (['region_head', 'field_officer', 'account_head'].includes(roleLower)) {
        newUserData.state_ids = Array.isArray(formData.state_ids)
          ? formData.state_ids
          : [];
      }
      
      if (roleLower === 'driver' && formData.driver_profile.license_number) {
        let licenseExpiry = null;
        if (formData.driver_profile.license_expiry) {
          if (formData.driver_profile.license_expiry.match(/^\d{4}\/\d{1,2}\/\d{1,2}$/)) {
            const [year, month, day] = formData.driver_profile.license_expiry.split('/');
            const paddedMonth = month.toString().padStart(2, '0');
            const paddedDay = day.toString().padStart(2, '0');
            licenseExpiry = `${year}-${paddedMonth}-${paddedDay}T00:00:00`;
          } else {
            licenseExpiry = formData.driver_profile.license_expiry;
          }
        }
        
        newUserData.driver_profile = {
          license_number: formData.driver_profile.license_number,
          license_expiry: licenseExpiry,
          transporter_id: formData.driver_profile.transporter_id || null
        };
        
        const currentUserRole = userProfile?.role?.toLowerCase();
        if ((currentUserRole === 'transporter' || currentUserRole === 'transporter') && userProfile?.id) {
          newUserData.driver_profile.transporter_id = userProfile.id;
        }
      } else if (roleLower === 'transporter' && formData.transporter_profile.company_name) {
        newUserData.transporter_profile = {
          company_name: formData.transporter_profile.company_name,
          registration_number: formData.transporter_profile.registration_number || null,
          contact_person: formData.transporter_profile.contact_person || null,
          address: formData.transporter_profile.address || null
        };
      } else if (roleLower === 'distributor' && formData.distributor_profile.company_name) {
        newUserData.distributor_profile = {
          company_name: formData.distributor_profile.company_name,
          contact_person: formData.distributor_profile.contact_person || null,
          address: formData.distributor_profile.address || null,
          district_id: formData.distributor_profile.district_id || null,
          latitude: formData.distributor_profile.latitude ? parseFloat(formData.distributor_profile.latitude) : null,
          longitude: formData.distributor_profile.longitude ? parseFloat(formData.distributor_profile.longitude) : null
        };
      } else if (roleLower === 'retailer' && formData.retailer_profile.company_name) {
        newUserData.retailer_profile = {
          company_name: formData.retailer_profile.company_name,
          contact_person: formData.retailer_profile.contact_person || null,
          address: formData.retailer_profile.address || null,
          latitude: formData.retailer_profile.latitude ? parseFloat(formData.retailer_profile.latitude) : null,
          longitude: formData.retailer_profile.longitude ? parseFloat(formData.retailer_profile.longitude) : null,
          state_id: formData.retailer_profile.state_id || null,
          district_id: formData.retailer_profile.district_id || null
        };
      }
      
      await onAddUser(newUserData);
      
      setFormData({
        username: '',
        email: '',
        phone_number: '',
        role: '',
        password: '',
        is_active: true,
        driver_profile: {
          license_number: '',
          license_expiry: '',
          transporter_id: ''
        },
        transporter_profile: {
          company_name: '',
          registration_number: '',
          contact_person: '',
          address: ''
        },
        distributor_profile: {
          company_name: '',
          contact_person: '',
          address: '',
          district_id: '',
          latitude: '',
          longitude: ''
        },
        retailer_profile: {
          company_name: '',
          contact_person: '',
          address: '',
          latitude: '',
          longitude: '',
          state_id: '',
          district_id: ''
        },
        port_ids: []
      });
      
      setErrors({});
    }
  };

  const handleCancel = () => {
    setFormData({
      username: '',
      email: '',
      phone_number: '',
      role: '',
      password: '',
      is_active: true,
      driver_profile: {
        license_number: '',
        license_expiry: '',
        transporter_id: ''
      },
      transporter_profile: {
        company_name: '',
        registration_number: '',
        contact_person: '',
        address: ''
      },
      distributor_profile: {
        company_name: '',
        contact_person: '',
        address: '',
        district_id: '',
        latitude: '',
        longitude: ''
      },
      retailer_profile: {
        company_name: '',
        contact_person: '',
        address: '',
        latitude: '',
        longitude: '',
        state_id: '',
        district_id: ''
      },
      port_ids: []
    });
    setErrors({});
    onCancel();
  };

  const currentUserRole = userProfile?.role?.toLowerCase();
  const isTransporter = currentUserRole === 'transporter';
  const roleLower = formData.role ? formData.role.toLowerCase() : '';
  const isDriverRole = roleLower === 'driver';
  const isPortAdminRole = roleLower === 'port_admin';

  return (
    <form onSubmit={handleSubmit} className="space-y-6 min-h-full">
      <div className=" p-1">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <CommonSelect
            label="Role"
            id="role"
            value={formData.role}
            onValueChange={(value) => handleChange('role', value)}
            options={roleOptions}
            error={errors.role}
            required
            placeholder="Select user role"
            searchable
          />
          
          <CommonInput
            label="Username"
            id="username"
            value={formData.username}
            onChange={(e) => handleChange('username', e.target.value)}
            error={errors.username}
            required
            placeholder="Enter username"
            autoComplete="new-username"
          />
          
          {!isDriverRole && !isPortAdminRole && (
            <CommonInput
              label={`Email ${formData.role === 'distributor' ? '(Optional)' : ''}`}
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              error={errors.email}
              required={formData.role !== 'distributor'}
              placeholder="Enter email address"
            />
          )}
          
          {isPortAdminRole && (
            <CommonInput
              label="Email"
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              error={errors.email}
              required
              placeholder="Enter email address"
            />
          )}
          
          <CommonInput
            label="Phone Number"
            id="phone_number"
            type="tel"
            value={formData.phone_number}
            onChange={(e) => {
              const inputValue = e.target.value;
          
              const sanitizedValue = inputValue.replace(/\D/g, '').slice(0, 10);
              handleChange('phone_number', sanitizedValue);
            }}
            onKeyPress={(e) => {
           
              if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                e.preventDefault();
              }
            }}
            error={errors.phone_number}
            required
            placeholder="Enter 10-digit phone number"
            maxLength={10}
          />
          
          {!isDriverRole && (
            <CommonInput
              label="Password"
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => handleChange('password', e.target.value)}
              error={errors.password}
              required
              placeholder="Enter password"
              autoComplete="new-password"
            />
          )}
          
          <CommonSelect
            label="Status"
            id="is_active"
            value={formData.is_active}
            onValueChange={(value) => handleChange('is_active', value === 'true' || value === true)}
            options={statusOptions}
            placeholder="Select user status"
            searchable
          />
        </div>
      </div>

      {isPortAdminRole && (
        <div className="border rounded-lg p-4">
          <h3 className="text-lg font-medium mb-4">Port Assignment</h3>
          <CommonMultiSelect
            label="Assigned Ports"
            id="port_ids"
            value={formData.port_ids}
            onChange={handlePortSelection}
            options={portOptions}
            error={errors.port_ids}
            required
            placeholder="Select Port"
            searchable
          />
        </div>
      )}

      {['region_head', 'field_officer', 'account_head'].includes(roleLower) && (
        <div className="border rounded-lg p-4">
          <h3 className="text-lg font-medium mb-4">State Assignment</h3>
          <CommonMultiSelect
            label="Assigned States"
            id="state_ids"
            value={formData.state_ids || []}
            onChange={(selectedStateIds) => setFormData(prev => ({ ...prev, state_ids: selectedStateIds }))}
            options={(masterData.states || []).map(state => ({
              value: state.id,
              label: state.name
            }))}
            error={errors.state_ids}
            required
            placeholder="Select State(s)"
            searchable
          />
        </div>
      )}

      {isDriverRole && (
        <DriverProfile
          driverProfile={formData.driver_profile}
          transporterOptions={transporterOptions}
          loading={loading}
          errors={errors}
          onProfileChange={handleProfileChange}
          currentUserRole={currentUserRole}
          currentUserId={userProfile?.id}
        />
      )}
      
      {roleLower === 'transporter' && (
        <TransporterProfile
          transporterProfile={formData.transporter_profile}
          errors={errors}
          onProfileChange={handleProfileChange}
        />
      )}

      {roleLower === 'distributor' && (
        <DistributorProfile
          distributorProfile={formData.distributor_profile}
          errors={errors}
          onProfileChange={handleProfileChange}
        />
      )}
      
      {roleLower === 'retailer' && (
        <RetailerProfile
          retailerProfile={formData.retailer_profile}
          errors={errors}
          onProfileChange={handleProfileChange}
        />
      )}
      
      <div className="flex justify-end space-x-3 pt-5">
        <CommonButton type="button" variant="outline" onClick={handleCancel}>
          Cancel
        </CommonButton>
        <CommonButton type="submit" variant="default" loading={isSubmitting}>
          {isTransporter ? 'Add Driver' : 'Add User'}
        </CommonButton>
      </div>
    </form>
  );
};

export default AddUserForm;